import pixLogo from "@/assets/pix-logo.svg";
import mpLogo from "@/assets/mercadopago-logo.svg";
import visaLogo from "@/assets/payment/visa.svg";
import mastercardLogo from "@/assets/payment/mastercard.svg";
import amexLogo from "@/assets/payment/amex.svg";
import eloLogo from "@/assets/payment/elo.svg";
import hipercardLogo from "@/assets/payment/hipercard.svg";
import stripeBadge from "@/assets/payment/stripe-badge.svg";
import { cn } from "@/lib/utils";

interface PaymentBadgesProps {
  /** Smaller size, no labels — for course cards */
  compact?: boolean;
  /** Hide the leading "Aceitamos:" / "We accept:" prefix */
  hideLabel?: boolean;
  /** Show the "Powered by Stripe" trust badge at the end */
  showStripeBadge?: boolean;
  className?: string;
}

const CARD_BRANDS = [
  { src: visaLogo, alt: "Visa" },
  { src: mastercardLogo, alt: "Mastercard" },
  { src: amexLogo, alt: "American Express" },
  { src: eloLogo, alt: "Elo" },
  { src: hipercardLogo, alt: "Hipercard" },
];

/**
 * Single inline card showing all accepted payment brands at a legible size.
 * Pix and Mercado Pago use the official wordmark SVGs and are rendered larger
 * so the brands are clearly recognizable.
 */
export function PaymentBadges({
  compact = false,
  hideLabel = false,
  showStripeBadge = false,
  className,
}: PaymentBadgesProps) {
  // Wordmark logos (Pix, Mercado Pago, Stripe) — rendered larger for legibility.
  const wordmarkSize = compact ? "h-7" : "h-10";
  // Card brand chips — kept proportionally smaller.
  const cardSize = compact ? "h-5" : "h-7";

  return (
    <div
      className={cn(
        "glass-surface glass-border inline-flex w-full flex-nowrap items-center gap-3 overflow-x-auto rounded-xl px-4 py-3 text-xs text-muted-foreground sm:gap-4",
        className,
      )}
      aria-label="Métodos de pagamento aceitos"
    >
      {!hideLabel && !compact && (
        <span className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Aceitamos
        </span>
      )}

      {/* Pix wordmark */}
      <img
        src={pixLogo}
        alt="Pix"
        className={cn(wordmarkSize, "w-auto shrink-0")}
        loading="lazy"
      />

      {/* Mercado Pago wordmark */}
      <img
        src={mpLogo}
        alt="Mercado Pago"
        className={cn(wordmarkSize, "w-auto shrink-0")}
        loading="lazy"
      />

      {/* Subtle divider */}
      <span className="hidden h-6 w-px shrink-0 bg-border/60 sm:inline-block" aria-hidden />

      {/* Card brand chips */}
      {CARD_BRANDS.map((brand) => (
        <img
          key={brand.alt}
          src={brand.src}
          alt={brand.alt}
          className={cn(cardSize, "w-auto shrink-0 rounded-[4px] shadow-sm")}
          loading="lazy"
        />
      ))}

      {showStripeBadge && (
        <>
          <span className="hidden h-6 w-px shrink-0 bg-border/60 sm:inline-block" aria-hidden />
          <img
            src={stripeBadge}
            alt="Stripe"
            className={cn(wordmarkSize, "w-auto shrink-0 opacity-90")}
            loading="lazy"
          />
        </>
      )}
    </div>
  );
}
